import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Building2, Store, MapPin, Euro, Star, Filter, ArrowUpRight, ArrowDownRight, BarChart3 } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ComposedChart, Line, Bar, Cell } from 'recharts';
const levels = [{
  id: 'konzern',
  name: 'Konzern-Übersicht',
  icon: Building2
}, {
  id: 'region',
  name: 'Regionen',
  icon: MapPin
}, {
  id: 'markt',
  name: 'Märkte',
  icon: Store
}];
const marketsData = [{
  id: 'hamburg-hafencity',
  name: 'ALDI Nord Hamburg-Hafencity',
  region: 'Nord',
  rating: 4.3,
  revenue: 8200,
  size: 1200,
  revenuePerSqm: 6833
}, {
  id: 'berlin-kreuzberg',
  name: 'ALDI Nord Berlin-Kreuzberg',
  region: 'Ost',
  rating: 3.8,
  revenue: 4100,
  size: 1000,
  revenuePerSqm: 4100
}, {
  id: 'hannover-mitte',
  name: 'ALDI Nord Hannover-Mitte',
  region: 'Nord',
  rating: 4.6,
  revenue: 6800,
  size: 1100,
  revenuePerSqm: 6181
}, {
  id: 'koeln-ehrenfeld',
  name: 'ALDI Nord Köln-Ehrenfeld',
  region: 'West',
  rating: 3.5,
  revenue: 3200,
  size: 900,
  revenuePerSqm: 3555
}, {
  id: 'frankfurt-sachsenhausen',
  name: 'ALDI Nord Frankfurt-Sachsenhausen',
  region: 'Mitte',
  rating: 4.4,
  revenue: 5900,
  size: 1050,
  revenuePerSqm: 5619
}, {
  id: 'dortmund-innenstadt',
  name: 'ALDI Nord Dortmund-Innenstadt',
  region: 'West',
  rating: 3.9,
  revenue: 4500,
  size: 950,
  revenuePerSqm: 4736
}, {
  id: 'leipzig-sued',
  name: 'ALDI Nord Leipzig-Süd',
  region: 'Ost',
  rating: 4.5,
  revenue: 7200,
  size: 1150,
  revenuePerSqm: 6260
}, {
  id: 'muenster-zentrum',
  name: 'ALDI Nord Münster-Zentrum',
  region: 'West',
  rating: 4.2,
  revenue: 5100,
  size: 1000,
  revenuePerSqm: 5100
}, {
  id: 'osnabrueck-zentrum',
  name: 'ALDI Nord Osnabrück-Zentrum',
  region: 'Nord',
  rating: 4.1,
  revenue: 4800,
  size: 980,
  revenuePerSqm: 4897
}, {
  id: 'kiel-zentrum',
  name: 'ALDI Nord Kiel-Zentrum',
  region: 'Nord',
  rating: 4.4,
  revenue: 5500,
  size: 1050,
  revenuePerSqm: 5238
}];
const regionsData = [{
  id: 'nord',
  name: 'ALDI Nord Region Nord',
  rating: 4.2,
  revenue: 17500,
  size: 4500,
  revenuePerSqm: 3888,
  markets: 3
}, {
  id: 'ost',
  name: 'ALDI Nord Region Ost',
  rating: 3.9,
  revenue: 8300,
  size: 4200,
  revenuePerSqm: 1976,
  markets: 2
}, {
  id: 'west',
  name: 'ALDI Nord Region West',
  rating: 3.87,
  revenue: 12900,
  size: 3500,
  revenuePerSqm: 3685,
  markets: 3
}, {
  id: 'mitte',
  name: 'ALDI Nord Region Mitte',
  rating: 4.4,
  revenue: 5900,
  size: 1600,
  revenuePerSqm: 3688,
  markets: 1
}];
const konzernData = {
  name: 'ALDI Nord Gesamtkonzern',
  rating: 4.15,
  revenue: 78700,
  size: 20550,
  revenuePerSqm: 3829,
  markets: 15,
  regions: 4
};
const timeSeriesData = [{
  month: 'Feb 23',
  rating: 4.05,
  revenue: 72500,
  revenuePerSqm: 3527
}, {
  month: 'Mär 23',
  rating: 4.08,
  revenue: 73200,
  revenuePerSqm: 3562
}, {
  month: 'Apr 23',
  rating: 4.06,
  revenue: 72800,
  revenuePerSqm: 3542
}, {
  month: 'Mai 23',
  rating: 4.1,
  revenue: 74500,
  revenuePerSqm: 3625
}, {
  month: 'Jun 23',
  rating: 4.12,
  revenue: 75800,
  revenuePerSqm: 3688
}, {
  month: 'Jul 23',
  rating: 4.09,
  revenue: 74200,
  revenuePerSqm: 3610
}, {
  month: 'Aug 23',
  rating: 4.11,
  revenue: 75100,
  revenuePerSqm: 3654
}, {
  month: 'Sep 23',
  rating: 4.14,
  revenue: 76500,
  revenuePerSqm: 3722
}, {
  month: 'Okt 23',
  rating: 4.13,
  revenue: 76800,
  revenuePerSqm: 3737
}, {
  month: 'Nov 23',
  rating: 4.16,
  revenue: 77500,
  revenuePerSqm: 3771
}, {
  month: 'Dez 23',
  rating: 4.18,
  revenue: 78200,
  revenuePerSqm: 3805
}, {
  month: 'Jan 24',
  rating: 4.2,
  revenue: 78700,
  revenuePerSqm: 3829
}];
const calculateCorrelation = (data: Array<{
  rating: number;
  revenuePerSqm: number;
}>) => {
  const n = data.length;
  const sumX = data.reduce((acc, d) => acc + d.rating, 0);
  const sumY = data.reduce((acc, d) => acc + d.revenuePerSqm, 0);
  const sumXY = data.reduce((acc, d) => acc + d.rating * d.revenuePerSqm, 0);
  const sumX2 = data.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const sumY2 = data.reduce((acc, d) => acc + d.revenuePerSqm * d.revenuePerSqm, 0);
  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  return denominator === 0 ? 0 : numerator / denominator;
};
const CustomScatterTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm" data-id="element-1313">
        <div className="font-semibold text-gray-900 mb-2" data-id="element-1314">{data.name}</div>
        <div className="space-y-1" data-id="element-1315">
          <div className="flex justify-between gap-4" data-id="element-1316">
            <span className="text-gray-500" data-id="element-1317">Rating:</span>
            <span className="font-medium" data-id="element-1318">{data.rating.toFixed(1)} ★</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1319">
            <span className="text-gray-500" data-id="element-1320">Umsatz/m²:</span>
            <span className="font-medium" data-id="element-1321">
              {data.revenuePerSqm.toLocaleString('de-DE')} €
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1322">
            <span className="text-gray-500" data-id="element-1323">Gesamtumsatz:</span>
            <span className="font-medium" data-id="element-1324">
              {(data.revenue / 1000).toFixed(1)}M €
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1325">
            <span className="text-gray-500" data-id="element-1326">Fläche:</span>
            <span className="font-medium" data-id="element-1327">
              {data.size.toLocaleString('de-DE')} m²
            </span>
          </div>
        </div>
      </div>;
  }
  return null;
};
export function StrategicAdvisorPage() {
  const [selectedLevel, setSelectedLevel] = useState<'konzern' | 'region' | 'markt'>('markt');
  const getScatterData = () => {
    switch (selectedLevel) {
      case 'markt':
        return marketsData.map(m => ({
          ...m,
          z: m.size
        }));
      case 'region':
        return regionsData.map(r => ({
          ...r,
          z: r.size
        }));
      case 'konzern':
        return [{
          ...konzernData,
          z: konzernData.size
        }];
    }
  };
  const scatterData = getScatterData();
  const correlation = calculateCorrelation(scatterData);
  const sortedByEfficiency = [...scatterData].sort((a, b) => b.revenuePerSqm - a.revenuePerSqm);
  const topPerformers = sortedByEfficiency.slice(0, 3);
  const bottomPerformers = sortedByEfficiency.slice(-3).reverse();
  const avgRevenuePerSqm = scatterData.reduce((acc, d) => acc + d.revenuePerSqm, 0) / scatterData.length;
  const potentialGain = scatterData.filter(d => d.revenuePerSqm < avgRevenuePerSqm).reduce((acc, d) => acc + (avgRevenuePerSqm - d.revenuePerSqm) * d.size / 1000, 0);
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-1328">
      <Card className="!p-4" data-id="element-1329">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-1330">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1331">
            <Filter size={16} data-id="element-1332" />
            <span data-id="element-1333">Ebene:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1334">
            {levels.map(level => {
            const isSelected = selectedLevel === level.id;
            const Icon = level.icon;
            return <button key={level.id} onClick={() => setSelectedLevel(level.id as any)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-aldi-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1335">
                  <Icon size={14} data-id="element-1336" />
                  {level.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1337">
        <KPICard title="Korrelation Rating ↔ Umsatz" value={correlation.toFixed(2)} change={correlation > 0.7 ? 'Stark positiv' : correlation > 0.4 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-1339" />} subtext="Pearson-Koeffizient" data-id="element-1338" />
        <KPICard title="Ø Umsatz pro m²" value={`${avgRevenuePerSqm.toLocaleString('de-DE', {
        maximumFractionDigits: 0
      })} €`} change="+4.2%" trend="up" icon={<Euro size={18} data-id="element-1341" />} subtext="vs. Vorjahr" data-id="element-1340" />
        <KPICard title="Ø Bewertung" value={(scatterData.reduce((acc, d) => acc + d.rating, 0) / scatterData.length).toFixed(2)} change="+0.15" trend="up" icon={<Star size={18} data-id="element-1343" />} subtext="Google Rating" data-id="element-1342" />
        <KPICard title="Optimierungspotenzial" value={`${potentialGain.toFixed(1)}M €`} change="bei Ø-Effizienz" trend="up" icon={<BarChart3 size={18} data-id="element-1345" />} subtext="Jahresumsatz" data-id="element-1344" />
      </div>

      <Card data-id="element-1346">
        <div className="p-6" data-id="element-1347">
          <div className="flex items-center justify-between mb-6" data-id="element-1348">
            <div data-id="element-1349">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1350">
                Rating vs. Umsatzeffizienz
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1351">
                Blasengröße = Verkaufsfläche (m²) •{' '}
                {selectedLevel === 'markt' ? 'Märkte' : selectedLevel === 'region' ? 'Regionen' : 'Konzern'}
              </p>
            </div>
          </div>
          <div className="h-96" data-id="element-1352">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1353">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-1354">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1355" />
                <XAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Google Rating (Sterne)',
                position: 'bottom',
                offset: 0,
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1356" />
                <YAxis type="number" dataKey="revenuePerSqm" name="Umsatz/m²" domain={[2000, 7000]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => `${(value / 1000).toFixed(1)}k`} label={{
                value: 'Umsatz pro m² (€)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1357" />
                <ZAxis type="number" dataKey="z" range={[100, 1000]} data-id="element-1358" />
                <Tooltip content={<CustomScatterTooltip data-id="element-1360" />} data-id="element-1359" />
                <Scatter name="Standorte" data={scatterData} fill="#00005f" data-id="element-1361">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.rating >= 4.3 ? '#639754' : entry.rating >= 3.8 ? '#00005f' : '#E03C32'} fillOpacity={0.7} data-id="element-1362" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1363">
        <Card data-id="element-1364">
          <div className="p-6" data-id="element-1365">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1366">
              <ArrowUpRight className="text-green-600" size={20} data-id="element-1367" /> Top
              Performer
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1368">
              Höchste Umsatzeffizienz (€/m²)
            </p>
            <div className="space-y-3" data-id="element-1369">
              {topPerformers.map((item, index) => <motion.div key={item.id || item.name} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-1370">
                  <div className="flex items-center gap-3" data-id="element-1371">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#639754'
                }} data-id="element-1372">
                      {index + 1}
                    </div>
                    <div data-id="element-1373">
                      <div className="font-medium text-gray-900 text-sm" data-id="element-1374">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1375">
                        {item.rating.toFixed(1)} ★ •{' '}
                        {item.size.toLocaleString('de-DE')} m²
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1376">
                    <div className="font-bold text-lg" style={{
                  color: '#639754'
                }} data-id="element-1377">
                      {item.revenuePerSqm.toLocaleString('de-DE')} €
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1378">pro m²</div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </Card>

        <Card data-id="element-1379">
          <div className="p-6" data-id="element-1380">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1381">
              <ArrowDownRight className="text-red-600" size={20} data-id="element-1382" />{' '}
              Optimierungspotenzial
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1383">
              Niedrigste Umsatzeffizienz (€/m²)
            </p>
            <div className="space-y-3" data-id="element-1384">
              {bottomPerformers.map((item, index) => <motion.div key={item.id || item.name} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-1385">
                  <div className="flex items-center gap-3" data-id="element-1386">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-1387">
                      {scatterData.length - index}
                    </div>
                    <div data-id="element-1388">
                      <div className="font-medium text-gray-900 text-sm" data-id="element-1389">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1390">
                        {item.rating.toFixed(1)} ★ •{' '}
                        {item.size.toLocaleString('de-DE')} m²
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1391">
                    <div className="font-bold text-lg" style={{
                  color: '#E03C32'
                }} data-id="element-1392">
                      {item.revenuePerSqm.toLocaleString('de-DE')} €
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1393">
                      pro m² (
                      {((avgRevenuePerSqm - item.revenuePerSqm) / avgRevenuePerSqm * 100).toFixed(0)}
                      % unter Ø)
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </Card>
      </div>

      <Card data-id="element-1394">
        <div className="p-6" data-id="element-1395">
          <div className="flex items-center justify-between mb-6" data-id="element-1396">
            <div data-id="element-1397">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1398">
                Entwicklung über Zeit
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1399">
                Konzern-Umsatz und Durchschnittsbewertung (12 Monate)
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1400">
              <div className="flex items-center gap-2" data-id="element-1401">
                <div className="w-3 h-3 rounded-full bg-aldi-blue" data-id="element-1402"></div>
                <span className="text-gray-600" data-id="element-1403">Umsatz (Mio. €)</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1404">
                <div className="w-6 h-0.5 bg-aldi-accent" data-id="element-1405"></div>
                <span className="text-gray-600" data-id="element-1406">Rating</span>
              </div>
            </div>
          </div>
          <div className="h-72" data-id="element-1407">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1408">
              <ComposedChart data={timeSeriesData} data-id="element-1409">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-1410" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-1411" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[70000, 82000]} tickFormatter={value => `${(value / 1000).toFixed(0)}`} label={{
                value: 'Umsatz (Mio. €)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1412" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[3.9, 4.4]} label={{
                value: 'Rating',
                angle: 90,
                position: 'insideRight',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1413" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [name === 'revenue' ? `${(value / 1000).toFixed(1)} Mio. €` : value.toFixed(2), name === 'revenue' ? 'Umsatz' : 'Rating']} data-id="element-1414" />
                <Bar yAxisId="left" dataKey="revenue" fill="#00005f" radius={[4, 4, 0, 0]} barSize={30} data-id="element-1415" />
                <Line yAxisId="right" type="monotone" dataKey="rating" stroke="#009ee3" strokeWidth={3} dot={{
                fill: '#009ee3',
                strokeWidth: 2,
                r: 4
              }} data-id="element-1416" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <Card data-id="element-1417">
        <div className="p-6" data-id="element-1418">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1419">
            Detailübersicht{' '}
            {selectedLevel === 'markt' ? 'Märkte' : selectedLevel === 'region' ? 'Regionen' : 'Konzern'}
          </h3>
          <div className="overflow-x-auto" data-id="element-1420">
            <table className="w-full text-sm" data-id="element-1421">
              <thead data-id="element-1422">
                <tr className="border-b border-gray-200" data-id="element-1423">
                  <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1424">
                    Name
                  </th>
                  {selectedLevel === 'markt' && <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1425">
                      Region
                    </th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1426">
                    Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1427">
                    Umsatz
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1428">
                    Fläche
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1429">
                    €/m²
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1430">
                    vs. Ø
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1431">
                {scatterData.map((item: any, index) => {
                const diff = (item.revenuePerSqm - avgRevenuePerSqm) / avgRevenuePerSqm * 100;
                return <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1432">
                      <td className="py-3 px-4 font-medium text-gray-900" data-id="element-1433">
                        {item.name}
                      </td>
                      {selectedLevel === 'markt' && <td className="py-3 px-4 text-gray-500" data-id="element-1434">
                          {item.region}
                        </td>}
                      <td className="py-3 px-4 text-right" data-id="element-1435">
                        <span className={`font-medium ${item.rating >= 4.3 ? 'text-green-600' : item.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-1436">
                          {item.rating.toFixed(1)} ★
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900" data-id="element-1437">
                        {(item.revenue / 1000).toFixed(1)}M €
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500" data-id="element-1438">
                        {item.size.toLocaleString('de-DE')} m²
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-gray-900" data-id="element-1439">
                        {item.revenuePerSqm.toLocaleString('de-DE')} €
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1440">
                        <span className={`font-medium ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1441">
                          {diff >= 0 ? '+' : ''}
                          {diff.toFixed(1)}%
                        </span>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}